/*
 * Project: bellcloud
 * File: packages/ui/components/common/DayTag.tsx
 * Author: SWAN
 * Created: 2026-03-05
 * Description: D-day 표시용 전용 Tag 컴포넌트
 */

import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { SystemStatusGroup } from '@packages/types/device/common/system_status_group';

// ==================== Types ====================
type DayTagColor = 'danger' | 'warning' | 'stable' | 'default';

interface DayTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** 색상 variant */
  color?: DayTagColor;
  /** SystemStatusGroup으로 색상 자동 결정 (color보다 우선) */
  statusGroup?: SystemStatusGroup;
  /** 태그 크기 */
  size?: 's' | 'm' | 'l';
  /** D-day 일수 (백엔드 기준: 양수=남은 날, 음수=지난 날, 0=오늘) */
  days: number;
  /** 아이콘 요소 */
  icon?: React.ReactNode;
}

/**
 * SystemStatusGroup을 DayTag color로 매핑
 * - GroupA (안전) -> stable
 * - GroupB (확인필요) -> warning
 * - GroupC (우선순위낮음) -> default
 * - GroupD (위험) -> danger
 */
function getColorFromStatusGroup(group: SystemStatusGroup): DayTagColor {
  switch (group) {
    case SystemStatusGroup.GroupA:
      return 'stable';
    case SystemStatusGroup.GroupB:
      return 'warning';
    case SystemStatusGroup.GroupC:
      return 'default';
    case SystemStatusGroup.GroupD:
      return 'danger';
    default:
      return 'default';
  }
}

// ==================== Component ====================
export const DayTag: React.FC<DayTagProps> = ({
  color,
  statusGroup,
  size = 'm',
  days,
  icon,
  ...props
}) => {
  // 색상 결정 우선순위:
  // 1. statusGroup이 있으면 그것으로 결정
  // 2. color prop이 명시되면 사용
  // 3. 없으면 days 값에 따라 자동 결정
  // 백엔드 기준: 양수 = 남은 날, 음수 = 지난 날, 0 = 오늘
  const getAutoColor = (): DayTagColor => {
    if (days < 0) return 'danger'; // 지난 날 (음수) - 위험
    if (days === 0) return 'warning'; // 오늘 - 주의
    if (days <= 7) return 'warning'; // 7일 이내 - 주의
    return 'stable'; // 남은 날 (양수) - 안전
  };

  const finalColor = statusGroup ? getColorFromStatusGroup(statusGroup) : color || getAutoColor();

  // displayText: positive = N days left, negative = N days ago, 0 = Today
  const displayText = days > 0 ? `${days} days left` : days === 0 ? 'Today' : `${Math.abs(days)} days ago`;

  return (
    <StyledDayTag color={finalColor} size={size} {...props}>
      {icon && <IconWrapper size={size}>{icon}</IconWrapper>}
      {displayText}
    </StyledDayTag>
  );
};

// ==================== Size Config ====================
const getSizeStyles = (theme: any, size: DayTagProps['size']) => {
  switch (size) {
    case 's':
      return css`
        padding: ${theme.spacing[1]}px ${theme.spacing[3]}px;
        font-size: 11px;
        line-height: 16px;
      `;
    case 'm':
      return css`
        padding: ${theme.spacing[2]}px ${theme.spacing[4]}px;
        font-size: 12px;
        line-height: 18px;
      `;
    case 'l':
      return css`
        padding: ${theme.spacing[3]}px ${theme.spacing[5]}px;
        font-size: 14px;
        line-height: 20px;
      `;
    default:
      return css`
        padding: ${theme.spacing[2]}px ${theme.spacing[4]}px;
        font-size: 12px;
        line-height: 18px;
      `;
  }
};

// ==================== Styled Components ====================
const StyledDayTag = styled('span', {
  shouldForwardProp: prop => prop !== 'color' && prop !== 'size',
})<{
  color: DayTagProps['color'];
  size: DayTagProps['size'];
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  white-space: nowrap;

  /* Size styles */
  ${({ size = 'm', theme }) => getSizeStyles(theme, size)}

  /* Color styles */
  ${({ color = 'default', theme }) => {
    switch (color) {
      case 'danger':
        return css`
          color: ${theme.colors.status.danger.stroke.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.danger.stroke.border};
        `;

      case 'warning':
        return css`
          color: ${theme.colors.status.caution.stroke.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.caution.stroke.border};
        `;

      case 'stable':
        return css`
          color: ${theme.colors.status.stable.stroke.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.stable.stroke.border};
        `;

      case 'default':
      default:
        return css`
          color: ${theme.colors.text.primary};
          border: ${theme.borderWidth.md}px solid ${theme.colors.border.container};
        `;
    }
  }}
`;

const IconWrapper = styled.span<{ size: DayTagProps['size'] }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: inherit;

  ${({ size = 'm' }) => {
    switch (size) {
      case 's':
        return css`
          width: 10px;
          height: 10px;
        `;
      case 'm':
        return css`
          width: 12px;
          height: 12px;
        `;
      case 'l':
        return css`
          width: 14px;
          height: 14px;
        `;
      default:
        return css`
          width: 12px;
          height: 12px;
        `;
    }
  }}

  img,
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;
